import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../components/Toast/toast.ts";
import axios from "axios";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useMediaQuery } from "@mui/material";
import IncidentVideoModal from "./IncidentVideoModal.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import { DataTable } from "../../components/DataTable/index.ts";
import type { Column } from "../../components/DataTable/index.ts";
import { StatusChip } from "../../components/StatusChip/index.ts";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import SearchBar from "./SearchBar.tsx";
import VideoDialog from "../../components/VideoDialog/VideoDialog.tsx";
import VideoSidebar from "../../components/VideoDialog/VideoSidebar.tsx";

const EvidenceRequestTable: React.FC = ({
  evidenceRequestData,
  loading,
  setEvidenceRequestData,
  isAllEvidenceRequestDataFetched,
  setIsAllEvidenceRequestDataFetched,
  setLoading,
}: any) => {
  const [visible, setVisible] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState("");
  const [innerLoading, setInnerLoading] = useState(false);
  const [currentCount, setCurrentCount] = useState(10);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { globalstorevalue } = useUserContext();
  const storedToken = localStorage.getItem("token");

  const normalizedData = (evidenceRequestData || []).flatMap((item: any) =>
    (item?.media || [])
      .filter((m: any) => m?.file_url && m?.poster_url)  // ✅ only valid media
      .map((m: any) => ({
        ...item,
        video_link: m.file_url,
        poster_url: m.poster_url,
        store_name: item.store_name || "Unknown Store",
        reason: item.evidence_details
          ? item.evidence_details.substring(0, 60) +
            (item.evidence_details.length > 60 ? "…" : "")
          : "",
      }))
  );

  const handleCloseDialog = () => {
    setDialogVisible(false);
  };

  const searchData = async () => {
    try {
      if (storedToken) {
        setLoading(true);
        setIsAllEvidenceRequestDataFetched(false);
        setCurrentCount(10);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/cust-portal/native/evidence-request${searchVal ? `?search=${searchVal}` : ""}`,
          { headers: { Authorization: `Bearer ${storedToken}` } },
        );
        setEvidenceRequestData(response?.data);
        if (response?.data?.length < 10)
          setIsAllEvidenceRequestDataFetched(true);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred.",
      );
      setLoading(false);
    }
  };

  const fetchMoreData = async () => {
    if (isAllEvidenceRequestDataFetched) return;
    const newRows = currentCount + 20;
    setInnerLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/native/evidence-request?first=0&rows=${newRows}${globalstorevalue ? `&stores=${globalstorevalue}` : ""}${searchVal ? `&search=${searchVal}` : ""}`,
        { headers: { Authorization: `Bearer ${storedToken}` } },
      );
      const newData = response?.data || [];
      if (newData.length < newRows) setIsAllEvidenceRequestDataFetched(true);
      setEvidenceRequestData(newData);
      setCurrentCount(newRows);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred.",
      );
    } finally {
      setInnerLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dateString.substring(0, dateString.indexOf("T"));
    const time = dateString.substring(
      dateString.indexOf("T") + 1,
      dateString.indexOf("Z") - 4,
    );
    const parts = date.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]} ${time}`;
  };

  const handleRowClick = (row: any) => {
    if (row?._id) navigate(`/evidence-request/${row._id}`);
  };

  const columns: Column[] = [
    {
      key: "index",
      label: "S.No",
      width: "5%",
      render: (_: any, __: any, index: number) => (
        <span className="text-[rgb(var(--text-muted))] text-[12px]">
          {index + 1}
        </span>
      ),
    },
    {
      key: "store_name",
      label: "Store Name",
      width: "15%",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[rgb(var(--text-primary))] text-[13px]">
          {value}
        </span>
      ),
    },
    {
      key: "created_on",
      label: "Incident Date",
      width: "15%",
      sortable: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: "evidence_status",
      label: "Status",
      width: "10%",
      sortable: true,
      render: (value: string) => {
        let variant: "success" | "warning" | "danger" | "info" | "neutral" =
          "neutral";
        if (value === "Completed" || value === "Sent") variant = "success";
        else if (value === "Pending") variant = "danger";
        const label =
          value === "Pending"
            ? "Requested"
            : value === "Completed" || value === "Sent"
              ? "Resolved"
              : value || "N/A";
        return <StatusChip label={label} variant={variant} />;
      },
    },
    {
      key: "evidence_subject",
      label: "Subject",
      width: "16%",
      sortable: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">
          {value?.length > 60 ? value.substring(0, 60) + "..." : value || "N/A"}
        </span>
      ),
    },
    {
      key: "evidence_details",
      label: "Incident Details",
      width: "20%",
      sortable: true,
      fullWidthOnMobile: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">
          {value?.length > 80 ? value.substring(0, 80) + "..." : value || "N/A"}
        </span>
      ),
    },
    {
      key: "media",
      label: "Video Evidence",
      width: "15%",
      align: "center",
      render: (value: any[], row: any, index: number) => {
        const validMediaList = value?.filter((m: any) => m?.file_url && m?.poster_url);

        if (!validMediaList?.length) {
          return (
            <span className="text-[11px] text-[rgb(var(--text-muted))]">
              No Video Available
            </span>
          );
        }

        return (
          <div className="flex flex-wrap gap-1">
            {validMediaList.map((m: any, mediaIdx: number) => {
              const flatIndex = normalizedData.findIndex(
                (n: any) => n._id === row._id && n.video_link === m.file_url
              );
              return (
                <div
                  key={mediaIdx}
                  className="w-[70px] h-[45px] overflow-hidden rounded relative cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentVideoIndex(flatIndex !== -1 ? flatIndex : 0);
                    setDialogVisible(true);
                  }}
                >
                  <img
                    className="w-full h-full object-cover rounded"
                    src={m.poster_url}
                    alt={`media-${mediaIdx}`}
                  />
                  <PlayCircleIcon
                    sx={{
                      color: "white",
                      fontSize: "18px",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* ── Video Dialog ── */}
      <VideoDialog
        visible={dialogVisible}
        onHide={handleCloseDialog}
        data={normalizedData}
        currentIndex={currentVideoIndex}
        onIndexChange={(i) => setCurrentVideoIndex(i)}
        onLoadMore={fetchMoreData}
        isAllDataFetched={isAllEvidenceRequestDataFetched}
        defaultSidebarOpen={true}
        sidebarContent={(onSelect) => (
          <VideoSidebar
            data={normalizedData}
            currentIndex={currentVideoIndex}
            onSelect={(i) => {
              onSelect(i);
              setCurrentVideoIndex(i);
            }}
            onClose={handleCloseDialog}
            isAllDataFetched={isAllEvidenceRequestDataFetched}
            onLoadMore={fetchMoreData}
          />
        )}
      />
      <IncidentVideoModal visible={visible} setVisible={setVisible} />

      <DataTable
        columns={columns}
        data={evidenceRequestData || []}
        rowKey="_id"
        loading={loading}
        skeletonRows={5}
        sortable={true}
        emptyMessage="No evidence request data available."
        maxHeight="calc(100vh - 380px)"
        onScrollEnd={fetchMoreData}
        hasMore={!isAllEvidenceRequestDataFetched}
        loader={
          <div className="flex justify-center py-4">
            <span className="text-[12px] text-[rgb(var(--text-muted))]">
              Loading more...
            </span>
          </div>
        }
        endMessage={<span>No More Data</span>}
        onRowClick={handleRowClick}
        header={
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <DescriptionBox
              title="Client Queries"
              subTitle="Evidence-Request Queries"
              description="Track and manage evidence requests across all store locations."
            />
            <div className="flex flex-wrap items-center gap-2">
              <SearchBar
                searchVal={searchVal}
                setSearchVal={setSearchVal}
                searchData={searchData}
              />
              <button
                style={{background: "var(--gold-grad)"}}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-gold-3 text-white text-[12px] font-medium transition-colors whitespace-nowrap"
                onClick={() => setVisible(true)}
              >
                + Create New Query
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default EvidenceRequestTable;
